import { useState } from 'react';
import { Box, Typography, Paper, Button, Alert } from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';
import vouchersService from '../../services/vouchersService';
import AppSnackbar from './AppSnackbar';
import { colors, designTokens } from '../../theme/theme';

interface PivotOffer {
  voucher_id: string;
  original_event_title: string;
  alternative_event_id: string;
  alternative_event_title: string;
  alternative_venue_name: string;
  spots_available: number;
}

interface Props {
  offer: PivotOffer;
  onResolved: () => void;
}

export default function PivotOfferBanner({ offer, onResolved }: Props) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<'accepted' | 'declined' | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' | 'info' });

  const handleAccept = async () => {
    setLoading(true);
    try {
      await vouchersService.acceptPivot(offer.voucher_id, offer.alternative_event_id);
      setResult('accepted');
      setTimeout(onResolved, 2000);
    } catch (e: any) { console.warn('[PivotOfferBanner] accept pivot failed', e); setSnackbar({ open: true, message: e.message, severity: 'error' }); }
    finally { setLoading(false); }
  };

  const handleDecline = async () => {
    setLoading(true);
    try {
      await vouchersService.declinePivot(offer.voucher_id);
      setResult('declined');
      setTimeout(onResolved, 2000);
    } catch (e: any) { console.warn('[PivotOfferBanner] decline pivot failed', e); setSnackbar({ open: true, message: e.message, severity: 'error' }); }
    finally { setLoading(false); }
  };

  if (result === 'accepted') return <Alert severity="success" sx={{ mb: 2 }}>Pivot accepted! You've been moved to "{offer.alternative_event_title}"</Alert>;
  if (result === 'declined') return <Alert severity="info" sx={{ mb: 2 }}>Pivot declined. Full refund is on its way.</Alert>;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 3, mb: 3,
          bgcolor: colors.warning.subtle,
          border: `1px solid ${designTokens.glass.border}`,
          borderRadius: `${designTokens.radius.lg}px`,
          boxShadow: '0 0 24px rgba(251,191,36,0.15)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SwapHoriz sx={{ color: colors.warning.main }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>Table Didn't Fill</Typography>
        </Box>
        <Typography variant="body2" sx={{ mb: 2, color: colors.neutral[900] }}>
          Your table for "<strong>{offer.original_event_title}</strong>" didn't reach minimum. But we found an alternative:
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2, mb: 2,
            bgcolor: designTokens.glass.surface,
            border: `1px solid ${designTokens.glass.border}`,
            borderRadius: `${designTokens.radius.md}px`,
          }}
        >
          <Typography sx={{ fontWeight: 600, color: '#FFFFFF' }}>{offer.alternative_event_title}</Typography>
          <Typography variant="body2" sx={{ color: colors.neutral[500] }}>at {offer.alternative_venue_name} · {offer.spots_available} spots available</Typography>
        </Paper>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={handleAccept} disabled={loading}
            sx={{ bgcolor: colors.warning.dark, '&:hover': { bgcolor: colors.warning.main } }}>
            {loading ? 'Processing...' : 'Switch to This Table'}
          </Button>
          <Button variant="outlined" onClick={handleDecline} disabled={loading}>
            Get Full Refund
          </Button>
        </Box>
      </Paper>
      <AppSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} />
    </>
  );
}
